import { inject, InjectionToken } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  CrmFieldService,
  CompanyCustomColumns,
  TaskCustomColumns,
  ContactCustomColumns,
  StandardExternalIds,
  StandardIds,
  SystemFieldIds,
} from '@galaxy/crm/services/field-service';
import {
  CategoriesCellComponent,
  CategoriesFilterChipComponent,
  CategoriesFilterInputComponent,
  CompanyAssociationPanelComponent,
  CompanyCustomFilterChipComponent,
  CompanyCustomFilterInputComponent,
  CompanyToContactAssociationsCardComponent,
  CompanyToCustomObjectAssociationsCardComponent,
  CompanyToFileAssociationsCardComponent,
  CompanyToOpportunityAssociationsPanelComponent,
  ContactAssociationPanelComponent,
  ContactCampaignsCardComponent,
  ContactToFileAssociationsCardComponent,
  CrmDependencies,
  CRMFilterOverride,
  CrmMultiLocationDependencies,
  CrmObjectDisplayService,
  CrmPipelineService,
  CustomObjectAssociationsComponent,
  CustomObjectToCompanyAssociationsPanelComponent,
  CustomObjectToContactAssociationsPanelComponent,
  CustomObjectToFileAssociationsCardComponent,
  CustomObjectToOpportunityAssociationsPanelComponent,
  DueDateCellComponent,
  DurationCustomFilterChipComponent,
  DurationCustomFilterInputComponent,
  FormDefaultInputValue,
  HiddenTextFormInputComponent,
  MultiLocationContext,
  MultiRowAction,
  ObjectType,
  SingleRowAction,
  OpportunityAssociationPanelComponent,
  OpportunityToFileAssociationsCardComponent,
  ParentChildCompanyAssociationsCardComponent,
  PresetFilter,
  ProfileCard,
  ProfileHeader,
  TableCustomCell,
  TeamMemberFilterChipComponent,
  TeamMemberFilterInputComponent,
  TranslateForCrmObjectService,
  UserCellComponent,
  UserFilterChipComponent,
  UserFilterInputComponent,
} from '@galaxy/crm/static';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BusinessCenterApiService } from '@vendasta/business-center';
import { combineLatest, filter, map, Observable, of, shareReplay, startWith, switchMap, firstValueFrom } from 'rxjs';
import { editionIds, partnerId, productIds } from '../../globals';
import { AccountGroupService } from '../account-group';
import { AccountsService } from '@vendasta/accounts/legacy';
import { AuthService } from '../auth.service';
import { FeatureFlagService } from '../core/feature-flag.service';
import { catchError, distinctUntilChanged } from 'rxjs/operators';
import { AddContactsToCampaignRedirectService } from '@galaxy/campaign/shared';
import { TranslateService } from '@ngx-translate/core';
import { ProductService } from '../core/product.service';
import { isBrand, LocationsService } from '../locations';
import { CRMUserService } from './user.service';
import { AdditionalTeamMemberFilterInputComponent } from './custom-filters/team-member/team-member-filter-input.component';
import { TeamMemberFormInputComponent } from './custom-form-inputs/team-member/team-member-form-input.component';
import { AdditionalTeamMemberFormInputComponent } from './custom-form-inputs/team-member/additional-team-member-form-input.component';
import { OwnerCellComponent } from './custom-cells/team-member/owner.component';
import { TeamMemberCellComponent } from './custom-cells/team-member/team-member.component';
import { CRMAccessService } from './access.service';
import { CrmSkuAccessService } from './crm-sku-access.service';
import { WhitelabelService } from '@galaxy/partner';
import { WhitelabelTranslationService } from '@galaxy/snapshot';
import { Feature, PartnerMarketConfigService } from '../partner-market-config';
import { ConversationChannel } from '@vendasta/conversation';
import {
  CrmUserColumnCellComponent,
  CrmUserFilterChipComponent,
  CrmUserFilterInputComponent,
  CrmUserInputComponent,
} from '@galaxy/crm/components/user';
import { addMonths } from 'date-fns';
import { CurrencyService } from '../currency';
import {
  buildContactMultiRowTableActions,
  buildContactSelectAllTableActions,
  buildContactSingleRowTableActions,
} from './contact-actions';
import {
  EmailWorkflowService,
  EMAIL_INTEGRATION_TYPE_GMAIL,
  EMAIL_INTEGRATION_TYPE_OUTLOOK,
  ProviderConnectionOption,
} from '@galaxy/crm/integrations/email-actions';
import { ListActionsService } from '@galaxy/crm/integrations/dynamic-lists-actions';
import { CampaignsProDemo, CampaignsProProd, EphemeralAutomationsFeature } from './constants';
import {
  buildCompanyMultiRowTableActions,
  buildCompanySelectAllTableActions,
  buildCompanySingleRowTableActions,
} from './company-actions';
import { HostService, IsCalendarConfiguredRequestInterface } from '@vendasta/meetings';
import {
  ConnectionStatus,
  ConnectionsApiService,
  ListConnectionRequest,
  ListConnectionRequestFilters,
} from '@vendasta/platform-integrations';
import { MeetingEventService } from '@galaxy/crm/integrations/meeting-event';
import { InboxService } from './inbox.service';
import { CacheFactory, CacheInjectionOptions, CacheStrategy } from '@galaxy/crm/components/cache';
import { ExportActionsService, BulkDeleteService, BulkDeleteAll, ExportSelectAllRowAction } from '@galaxy/crm/dynamic';
import {
  CrmPipelineColumnCellComponent,
  CrmPipelineFilterChipComponent,
  CrmPipelineFilterInputComponent,
  CrmStageColumnCellComponent,
} from '@galaxy/crm/components/pipeline';
import { NpsBadgeComponent } from '@galaxy/crm/integrations/reputation';
import { LanguageService } from '../core/language-service.service';
import { AutomationActionsService } from '@galaxy/crm/integrations/automation';
import { LeadScoreBadgeComponent } from '@galaxy/crm/components/badge';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ReputationService } from '../reputation/reputation.service';
import { Brand } from '../brands/brand';
import {
  ActivityAssociationInterface,
  CRMCustomObjectTypeApiService,
  CrmObjectInterface,
  CustomObjectType,
} from '@vendasta/crm';
import { GuardsCheckStart, Router } from '@angular/router';
import { CampaignActionsService } from '@galaxy/crm/integrations/yesware';
import { GalaxyDateDefault, GalaxyFilterInterface, GalaxyFilterOperator } from '@vendasta/galaxy/filter/chips';
import { AiAssistantService, DEFAULT_AVATAR_SVG_ICON } from '@galaxy/ai-assistant';
import { VoiceApiService } from '@vendasta/voice';
import { AutomationsSideMenuDrawerComponent } from '@galaxy/automata';
import { ListItemsApiService } from '@vendasta/bulk-actions';
import { EntityType } from '@vendasta/automata';
import { Row } from '@vendasta/galaxy/table';
import { NamespaceResolutionService } from './namespace-resolution.service';
import { extractAccountGroupIdFromRoute } from './extract-account-group-id.util';
import { SnapshotSidepanelService } from './snapshot-sidepanel/snapshot-sidepanel.service';
import { ProductsComponent } from './company-details/products/products.component';
import { AssociatedAccountComponent } from './company-details/associated-account/associated-account.component';
import { ReportsComponent } from './company-details/reports/reports.component';
import { SalesOrdersComponent } from './company-details/sales-orders/sales-orders.component';

export const CrmInjectionToken = new InjectionToken<CrmDependencies>('dependencies given to crm library', {
  factory: () => {
    const cacheStrategy: CacheStrategy = { maxAge: 20000, observable: { share: true }, autoCleanup: true };
    const crmUserServiceCacheOptions: CacheInjectionOptions<CRMUserService> = {
      methods: [{ method: 'getMultiUsers', strategy: cacheStrategy }],
    };
    const userService = CacheFactory.inject(CRMUserService, crmUserServiceCacheOptions);
    const customObjectTypeService = inject(CRMCustomObjectTypeApiService, { optional: true });

    const pipelineCacheStrategy: CacheStrategy = { maxAge: 20000, autoCleanup: true };
    const crmPipelineServiceCacheOptions: CacheInjectionOptions<CrmPipelineService> = {
      methods: [
        { method: 'getMultiPipeline$', strategy: pipelineCacheStrategy },
        { method: 'getPipeline$', strategy: pipelineCacheStrategy },
      ],
    };
    const pipelineService = CacheFactory.inject(CrmPipelineService, crmPipelineServiceCacheOptions);

    const aiAssistantService = inject(AiAssistantService, { optional: true });
    const voiceApiService = inject(VoiceApiService);

    const translationService = inject(TranslateService);
    const translateForObjectService = inject(TranslateForCrmObjectService);
    const accountGroupService = inject(AccountGroupService);
    const featureFlagService = inject(FeatureFlagService);
    const businessCenterApiService = inject(BusinessCenterApiService);
    const contactCampaignService = inject(AddContactsToCampaignRedirectService);
    const authService = inject(AuthService);
    const activeAppService = inject(ProductService);
    const accessService = inject(CRMAccessService);
    const crmSkuAccessService = inject(CrmSkuAccessService);
    const emailWorkflowService = inject(EmailWorkflowService);
    const whitelabelService = inject(WhitelabelService);
    const locationsService = inject(LocationsService);
    const crmFieldService = inject(CrmFieldService);
    const productAnalyticsService = inject(ProductAnalyticsService);
    const partnerMarketConfigService = inject(PartnerMarketConfigService);
    const currencyService = inject(CurrencyService);
    const crmInboxService = inject(InboxService);
    const listActionsService = inject(ListActionsService);
    const bulkDeleteService = inject(BulkDeleteService);
    const meetingService = inject(HostService);
    const meetingEventService = inject(MeetingEventService);
    const exportActionsService = inject(ExportActionsService);
    const languageService = inject(LanguageService);
    const automationActionsService = inject(AutomationActionsService);
    const accountsService = inject(AccountsService);
    const reputationService = inject(ReputationService);
    const confirmationModalService = inject(OpenConfirmationModalService);
    const snackbarService = inject(SnackbarService);
    const crmObjectDisplayService = inject(CrmObjectDisplayService);
    const contactPhoneFieldID = crmFieldService.getFieldId(StandardExternalIds.PhoneNumber);
    const emailFieldID = crmFieldService.getFieldId(StandardExternalIds.Email);
    const tagsFieldID = crmFieldService.getFieldId(StandardExternalIds.Tags);
    const yeswareCampaignActionsService = inject(CampaignActionsService);
    const connectionsApiService = inject(ConnectionsApiService);
    const listItemsApiService = inject(ListItemsApiService);
    const namespaceResolutionService = inject(NamespaceResolutionService);
    const snapshotSidepanelService = inject(SnapshotSidepanelService);
    const whitelabelTranslationService = inject(WhitelabelTranslationService);

    const namespace$ = namespaceResolutionService.namespace$;
    const parentNamespace$ = namespaceResolutionService.parentNamespace$;
    const isResolvedToPartnerNamespace$ = namespaceResolutionService.isResolvedToPartnerNamespace$;
    const snapshotName$ = whitelabelTranslationService.stream('SNAPSHOT_REPORT.COMMON.SNAPSHOT_NAME');

    const configuration$ = locationsService.marketId$.pipe(
      switchMap((marketId) => whitelabelService.getConfiguration(partnerId, marketId)),
    );
    const showContacts$ = configuration$.pipe(map((config) => config.businessCenterConfiguration.showCustomers));
    const showCompanies$ = configuration$.pipe(map((config) => config.businessCenterConfiguration.showCrmCompanies));

    const companyToCompanyFeatureFlag$ = featureFlagService.checkFeatureFlag(
      partnerId,
      '',
      'crm_company_company_association',
    );
    const crmCustomObjectFeatureFlag$ = featureFlagService.checkFeatureFlag(partnerId, '', 'bcc_crm_custom_objects');
    const fileAssociationsFeatureFlag$ = featureFlagService.checkFeatureFlag(partnerId, '', 'crm_file_associations');

    const showOpportunities$ = configuration$.pipe(
      map((config) => {
        return config.businessCenterConfiguration.showCrmOpportunities;
      }),
    );

    const showCustomObjects$ = combineLatest([
      configuration$,
      crmCustomObjectFeatureFlag$,
      crmSkuAccessService.enableCustomObjects$,
    ]).pipe(
      map(([config, featureFlag, enableCustomObjects]) => {
        return config.businessCenterConfiguration.showCrmCustomObjects && featureFlag && enableCustomObjects;
      }),
    );

    const companyProfileCards$: Observable<ProfileCard[]> = combineLatest([
      companyToCompanyFeatureFlag$,
      showOpportunities$,
      showContacts$,
      showCustomObjects$,
      fileAssociationsFeatureFlag$,
      isResolvedToPartnerNamespace$,
    ]).pipe(
      map(
        ([
          companyCompanyAssociationsFlag,
          showOpportunities,
          showContacts,
          showCustomObjects,
          hasFileAssociations,
          isResolvedToPartner,
        ]) => {
          const cards = [] as ProfileCard[];
          if (showContacts) {
            cards.push({ component: CompanyToContactAssociationsCardComponent });
          }
          if (showOpportunities) {
            cards.push({ component: CompanyToOpportunityAssociationsPanelComponent });
          }
          if (companyCompanyAssociationsFlag) {
            cards.push({ component: ParentChildCompanyAssociationsCardComponent });
          }
          if (hasFileAssociations) {
            cards.push({ component: CompanyToFileAssociationsCardComponent });
          }
          if (showCustomObjects) {
            cards.push({ component: CompanyToCustomObjectAssociationsCardComponent });
          }
          if (isResolvedToPartner) {
            cards.push({ component: ProductsComponent });
            cards.push({ component: AssociatedAccountComponent });
            cards.push({ component: ReportsComponent });
            cards.push({ component: SalesOrdersComponent });
          }
          return cards;
        },
      ),
    );
    const contactProfileCards$ = combineLatest([
      showCompanies$,
      showOpportunities$,
      activeAppService.activeProducts$.pipe(startWith([])),
      showCustomObjects$,
      fileAssociationsFeatureFlag$,
    ]).pipe(
      map(([showCompanies, showOpportunities, activeProducts, showCustomObjects, hasFileAssociations]) => {
        const cards = [] as ProfileCard[];
        if (showCompanies) {
          cards.push({ component: CompanyAssociationPanelComponent });
        }
        if (showOpportunities) {
          cards.push({ component: OpportunityAssociationPanelComponent });
        }
        if (
          activeProducts.filter((app) => app.productId === CampaignsProProd || app.productId === CampaignsProDemo)
            .length != 0
        ) {
          cards.push({ component: ContactCampaignsCardComponent });
        }
        if (hasFileAssociations) {
          cards.push({ component: ContactToFileAssociationsCardComponent });
        }
        if (showCustomObjects) {
          cards.push({ component: CustomObjectAssociationsComponent });
        }
        return cards;
      }),
    );
    const opportunityProfileCards$ = combineLatest([
      showCompanies$,
      showContacts$,
      showCustomObjects$,
      fileAssociationsFeatureFlag$,
    ]).pipe(
      map(([showCompanies, showContacts, showCustomObjects, hasFileAssociations]) => {
        const cards = [] as ProfileCard[];
        if (showContacts) {
          cards.push({ component: ContactAssociationPanelComponent });
        }
        if (showCompanies) {
          cards.push({ component: CompanyAssociationPanelComponent });
        }
        if (hasFileAssociations) {
          cards.push({ component: OpportunityToFileAssociationsCardComponent });
        }
        if (showCustomObjects) {
          cards.push({ component: CustomObjectAssociationsComponent });
        }
        return cards;
      }),
    );

    const customObjectProfileCards$ = combineLatest([
      showCompanies$,
      showContacts$,
      showOpportunities$,
      showCustomObjects$,
      fileAssociationsFeatureFlag$,
    ]).pipe(
      map(([showCompanies, showContacts, showOpportunities, showCustomObjects, hasFileAssociations]) => {
        const cards = [] as ProfileCard[];
        if (showContacts) {
          cards.push({ component: CustomObjectToContactAssociationsPanelComponent });
        }
        if (showCompanies) {
          cards.push({ component: CustomObjectToCompanyAssociationsPanelComponent });
        }
        if (showOpportunities) {
          cards.push({ component: CustomObjectToOpportunityAssociationsPanelComponent });
        }
        if (hasFileAssociations) {
          cards.push({ component: CustomObjectToFileAssociationsCardComponent });
        }
        if (showCustomObjects) {
          cards.push({ component: CustomObjectAssociationsComponent });
        }
        return cards;
      }),
    );

    const isMeetingSchedulerConfigured$ = combineLatest([
      accountGroupService.currentAccountGroupId$,
      toObservable(authService.effectiveUserId),
    ]).pipe(
      map(([namespace, currentUser]) => {
        const request: IsCalendarConfiguredRequestInterface = {
          applicationContextProperties: {
            business_id: namespace,
            user_context: 'SMB',
          },
          userId: currentUser,
        };
        return request;
      }),
      switchMap((request) => meetingService.IsCalendarConfigured(request)),
      map((response) => {
        return response.isConfigured == true;
      }),
    );

    const currentUserId$ = toObservable(authService.effectiveUserId);
    const locale$ = languageService.currentLocale$;
    const rmPremiumActive$: Observable<boolean> = accountGroupService.currentAccountGroupId$.pipe(
      switchMap((accountGroupId) => accountsService.list(accountGroupId, partnerId)),
      map((res) => res.accounts || []),
      map((accounts) => accounts.filter((a) => !a.trial && !a.deactivation)),
      map((accounts) =>
        accounts.some(
          (account) =>
            account.productId === productIds.reputationManagement &&
            account.editionId === editionIds.reputationManagementPremium,
        ),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    const yeswareActive$: Observable<boolean> = accountGroupService.currentAccountGroupId$.pipe(
      switchMap((accountGroupId) => accountsService.list(accountGroupId, partnerId)),
      map((res) => res.accounts || []),
      map((accounts) => accounts.filter((a) => !a.trial && !a.deactivation)),
      map((accounts) => {
        return accounts.some((account) => account.productId === 'MP' && account.appId === productIds.yesware);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const buildConnectionOptions = (integrationType: string): Observable<ProviderConnectionOption[]> =>
      combineLatest([accountGroupService.currentAccountGroupId$, currentUserId$]).pipe(
        switchMap(([namespace, currentUserId]) => {
          const request = new ListConnectionRequest({
            namespace,
            filters: new ListConnectionRequestFilters({ integrationType }),
          });
          return connectionsApiService.list(request).pipe(
            map((response) =>
              (response.connections || [])
                .filter((c) => c.status === ConnectionStatus.CONNECTED && c.userId === currentUserId)
                .map((c) => ({ connectionId: c.connectionId, label: c.label || '', isDelegated: false })),
            ),
            catchError(() => of([] as ProviderConnectionOption[])),
          );
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

    const gmailConnectionOptions$ = buildConnectionOptions(EMAIL_INTEGRATION_TYPE_GMAIL);
    const gmailConnected$ = gmailConnectionOptions$.pipe(map((c) => c.length > 0));

    const outlookConnectionOptions$ = buildConnectionOptions(EMAIL_INTEGRATION_TYPE_OUTLOOK);
    const outlookConnected$ = outlookConnectionOptions$.pipe(map((c) => c.length > 0));

    return {
      appID: 'business-center-client',
      routePrefix$: accountGroupService.currentAccountGroupId$.pipe(
        map((accountGroupId) => `/account/location/${accountGroupId}/crm`),
      ),
      namespace$: namespace$,
      parentNamespace$: parentNamespace$,
      currentUserId$: currentUserId$,
      locale$: locale$,
      viewProfileFlag$: of(true),
      hasCrmAssociationModalFeatureFlag$: parentNamespace$.pipe(
        filter((parentNamespace) => !!parentNamespace),
        switchMap((parentNamespace) => {
          return featureFlagService.checkFeatureFlag(parentNamespace, '', 'crm_association_modal');
        }),
        startWith(false),
      ),
      hasRestrictedFieldValuesAdminFeatureFlag$: parentNamespace$.pipe(
        filter((parentNamespace) => !!parentNamespace),
        switchMap((parentNamespace) => {
          return featureFlagService.checkFeatureFlag(parentNamespace, '', 'crm_restricted_values_admin');
        }),
        startWith(false),
      ),
      hasCustomObjectFeatureFlag$: parentNamespace$.pipe(
        filter((parentNamespace) => !!parentNamespace),
        switchMap((parentNamespace) => {
          return featureFlagService.checkFeatureFlag(parentNamespace, '', 'bcc_crm_custom_objects');
        }),
        startWith(false),
      ),
      hasImportRecordFeatureFlag$: parentNamespace$.pipe(
        filter((parentNamespace) => !!parentNamespace),
        switchMap((parentNamespace) => {
          return featureFlagService.checkFeatureFlag(parentNamespace, '', 'crm_import_records');
        }),
        startWith(false),
      ),
      hasCustomObjectPipelineFeatureFlag$: parentNamespace$.pipe(
        filter((parentNamespace) => !!parentNamespace),
        switchMap((parentNamespace) => {
          return featureFlagService.checkFeatureFlag(parentNamespace, '', 'crm_custom_object_board');
        }),
        startWith(false),
      ),
      customObjectFeatureFlag: 'bcc_crm_custom_objects',
      hasTimelineActivitiesFeatureFlag$: parentNamespace$.pipe(
        filter((parentNamespace) => !!parentNamespace),
        switchMap((parentNamespace) => {
          return featureFlagService.checkFeatureFlag(parentNamespace, '', 'crm_timeline_activities');
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      ),
      showCustomObjects$: showCustomObjects$,
      activity: {
        filterInputOverrides$: filterActivityInputOverrides$(translationService),
        showFileAssociations$: fileAssociationsFeatureFlag$,
      },
      contact: {
        services: {
          campaignService: contactCampaignService,
        },
        showRightHandProfilePanel$: contactProfileCards$.pipe(map((cards) => cards.length > 0)),
        showOpportunityAssociation$: of(true),
        profileCards$: contactProfileCards$,
        createAssociationFields: {
          objectTypes: ['Company'],
          required: false,
        },
        presetFilters$: objectViewPresets$(
          translationService,
          translateForObjectService,
          currentUserId$,
          'Contact',
          crmFieldService.getFieldId(StandardIds.ContactLastEngagementDate),
          crmFieldService.getFieldId(SystemFieldIds.ContactOwnerID),
        ),
        filterInputOverrides$: filterContactInputOverrides$(translationService),
        initialFilters$: of([
          { fieldId: StandardExternalIds.Email, filterId: 'filterContactByEmail' },
          { fieldId: SystemFieldIds.ContactCreated, filterId: 'filterContactByCreated' },
          { fieldId: SystemFieldIds.ContactLastActivityDate, filterId: 'filterContactByLastActivity' },
          { fieldId: StandardIds.ContactLifecycleStage, filterId: 'filterContactByLifecycleStage' },
        ]),
        baseColumnIds: [
          ContactCustomColumns.FullName,
          ContactCustomColumns.PrimaryCompanyName,
          contactPhoneFieldID,
          emailFieldID,
          tagsFieldID,
          StandardIds.ContactSourceName,
          SystemFieldIds.ContactCreated,
        ],
        formCustomInputs: [
          {
            fieldId: SystemFieldIds.ContactGroupID,
            component: HiddenTextFormInputComponent,
          },
          {
            fieldId: SystemFieldIds.ContactOwnerID,
            component: TeamMemberFormInputComponent,
          },
          {
            fieldId: StandardIds.ContactAdditionalTeamMemberIDs,
            component: AdditionalTeamMemberFormInputComponent,
          },
        ],
        formDefaultOnCreateInputValues$: of([
          { fieldId: StandardIds.ContactSourceName, value: 'CRM UI' },
          { fieldId: StandardIds.ContactRecordSourceDrill1, value: window.location.href },
        ] as FormDefaultInputValue[]),
        tableCustomCells: contactTableCustomCells(translationService),
        singleRowTableActions$: buildContactSingleRowTableActions(
          contactCampaignService,
          productAnalyticsService,
          accountGroupService,
          featureFlagService,
          listActionsService,
          crmInboxService,
          accessService,
          automationActionsService,
          translationService,
          confirmationModalService,
          reputationService,
          snackbarService,
          rmPremiumActive$,
          yeswareCampaignActionsService,
          yeswareActive$,
          activeAppService,
          currentUserId$,
          authService.isSuperAdmin$,
          parentNamespace$,
          emailWorkflowService,
          gmailConnected$,
          outlookConnected$,
        ),
        multiRowTableActions$: buildContactMultiRowTableActions(
          contactCampaignService,
          accountGroupService,
          translationService,
          activeAppService,
          productAnalyticsService,
          featureFlagService,
          listActionsService,
          accessService,
          automationActionsService,
          confirmationModalService,
          reputationService,
          snackbarService,
          rmPremiumActive$,
          yeswareCampaignActionsService,
          yeswareActive$,
          currentUserId$,
          authService.isSuperAdmin$,
          parentNamespace$,
        ),
        selectAllTableActions$: buildContactSelectAllTableActions(
          contactCampaignService,
          translationService,
          activeAppService,
          productAnalyticsService,
          accountGroupService,
          exportActionsService,
          featureFlagService,
          listActionsService,
          bulkDeleteService,
          accessService,
          automationActionsService,
          confirmationModalService,
          businessCenterApiService,
          snackbarService,
          rmPremiumActive$,
          currentUserId$,
          authService.isSuperAdmin$,
          parentNamespace$,
        ),
        profileHeader$: buildContactHeaders$(partnerMarketConfigService, activeAppService),
        showHistoryButton$: authService.isSuperAdmin$.pipe(shareReplay({ refCount: true, bufferSize: 1 })),
      },
      company: {
        showRightHandProfilePanel$: companyProfileCards$.pipe(map((cards) => cards.length > 0)),
        showOpportunityAssociation$: of(true),
        profileCards$: companyProfileCards$,
        createAssociationFields: {
          objectTypes: ['Contact'],
          required: false,
        },
        presetFilters$: objectViewPresets$(
          translationService,
          translateForObjectService,
          currentUserId$,
          'Company',
          crmFieldService.getFieldId(StandardIds.CompanyLastEngagementDate),
          crmFieldService.getFieldId(SystemFieldIds.CompanyOwnerID),
        ),
        filterInputOverrides$: filterCompanyInputOverride$(translationService),
        initialFilters$: of([
          { fieldId: StandardIds.CompanyName, filterId: 'filterCompanyByName' },
          { fieldId: SystemFieldIds.CompanyCreated, filterId: 'filterCompanyByCreated' },
          { fieldId: SystemFieldIds.CompanyLastActivityDate, filterId: 'filterCompanyByLastActivity' },
          { fieldId: StandardIds.CompanyLifecycleStage, filterId: 'filterCompanyByLifecycleStage' },
        ]),
        baseColumnIds: [
          StandardIds.CompanyName,
          CompanyCustomColumns.FullAddress,
          StandardIds.CompanyPhoneNumber,
          SystemFieldIds.CompanyOwnerID,
          StandardIds.CompanyAdditionalTeamMemberIDs,
        ],
        formCustomInputs: [
          {
            fieldId: SystemFieldIds.CompanyGroupID,
            component: HiddenTextFormInputComponent,
          },
          {
            fieldId: SystemFieldIds.CompanyOwnerID,
            component: TeamMemberFormInputComponent,
          },
          {
            fieldId: StandardIds.CompanyAdditionalTeamMemberIDs,
            component: AdditionalTeamMemberFormInputComponent,
          },
        ],
        formDefaultOnCreateInputValues$: of([
          { fieldId: StandardIds.CompanySourceName, value: 'CRM UI' },
          { fieldId: StandardIds.CompanyRecordSourceDrill1, value: window.location.href },
        ] as FormDefaultInputValue[]),
        tableCustomCells: companyTableCustomCells(translationService),
        showCampaigns$: activeAppService.activeProducts$.pipe(
          map((apps) => {
            return (
              apps.filter((app) => app.productId === CampaignsProProd || app.productId === CampaignsProDemo).length > 0
            );
          }),
        ),
        singleRowTableActions$: buildCompanySingleRowTableActions(
          accountGroupService,
          featureFlagService,
          listActionsService,
          accessService,
          automationActionsService,
          currentUserId$,
          authService.isSuperAdmin$,
          parentNamespace$,
          snapshotSidepanelService,
          isResolvedToPartnerNamespace$,
          namespace$,
          snapshotName$,
        ),
        multiRowTableActions$: buildCompanyMultiRowTableActions(
          accountGroupService,
          featureFlagService,
          translationService,
          listActionsService,
          accessService,
          automationActionsService,
          currentUserId$,
          authService.isSuperAdmin$,
          parentNamespace$,
        ),
        selectAllTableActions$: buildCompanySelectAllTableActions(
          accountGroupService,
          featureFlagService,
          translationService,
          listActionsService,
          bulkDeleteService,
          exportActionsService,
          accessService,
          automationActionsService,
          currentUserId$,
          authService.isSuperAdmin$,
          parentNamespace$,
        ),
        showHistoryButton$: authService.isSuperAdmin$.pipe(shareReplay({ refCount: true, bufferSize: 1 })),
      },
      task: {
        filterInputOverrides$: of([
          {
            title: translationService.instant('CRM.CUSTOM_FILTERS.ASSIGNED_TO'),
            fieldId: SystemFieldIds.ActivityOwnerID,
            filterInput: UserFilterInputComponent,
            filterChip: UserFilterChipComponent,
          },
        ]),
        tableCustomCells: [
          {
            fieldIds: [SystemFieldIds.ActivityOwnerID],
            columnDefinition: {
              id: TaskCustomColumns.AssignedTo,
              title: translationService.instant('CRM.CUSTOM_CELLS.ASSIGNED_TO'),
            },
            customCellComponent: UserCellComponent,
          },
          {
            fieldIds: [StandardIds.ActivityTaskDueDate, StandardIds.ActivityTaskStatus],
            columnDefinition: {
              id: StandardIds.ActivityTaskDueDate,
            },
            customCellComponent: DueDateCellComponent,
          },
        ],
        selectAllTableActions$: bulkTaskSelectAllTableActions(accountGroupService, bulkDeleteService),
      },
      opportunity: {
        profileCards$: opportunityProfileCards$,
        formCustomInputs: [
          {
            fieldId: SystemFieldIds.OpportunityOwnerID,
            component: CrmUserInputComponent,
          },
        ],
        filterInputOverrides$: of([
          {
            title: translationService.instant('CRM.CUSTOM_FILTERS.OWNER'),
            fieldId: SystemFieldIds.OpportunityOwnerID,
            filterInput: CrmUserFilterInputComponent,
            filterChip: CrmUserFilterChipComponent,
          },
          {
            title: translationService.instant('CRM.CUSTOM_FILTERS.PIPELINE'),
            fieldId: StandardIds.OpportunityPipelineID,
            filterInput: CrmPipelineFilterInputComponent,
            filterChip: CrmPipelineFilterChipComponent,
          },
        ]),
        tableCustomCells: [
          {
            fieldIds: [SystemFieldIds.OpportunityOwnerID],
            columnDefinition: {
              id: SystemFieldIds.OpportunityOwnerID,
            },
            customCellComponent: CrmUserColumnCellComponent,
          },
          {
            fieldIds: [StandardIds.OpportunityPipelineID],
            columnDefinition: {
              id: StandardIds.OpportunityPipelineID,
            },
            customCellComponent: CrmPipelineColumnCellComponent,
          },
          {
            fieldIds: [StandardIds.OpportunityCalculatedStageID, StandardIds.OpportunityPipelineID],
            columnDefinition: {
              id: StandardIds.OpportunityCalculatedStageID,
            },
            customCellComponent: CrmStageColumnCellComponent,
          },
        ],
        createAssociationFields: {
          objectTypes: ['Contact', 'Company'],
          required: true,
        },
        formDefaultOnCreateInputValues$: combineLatest([currentUserId$, currencyService.getMarketCurrency()]).pipe(
          map(([userId, currencyCode]) => [
            {
              fieldId: StandardIds.OpportunityAmount,
              value: {
                currencyCode: currencyCode,
                amount: 0,
              },
            },
            {
              fieldId: SystemFieldIds.OpportunityOwnerID,
              value: userId,
            },
            {
              fieldId: StandardIds.OpportunityExpectedCloseDate,
              value: addMonths(new Date(), 1),
            },
            {
              fieldId: StandardIds.OpportunityStatus,
              value: 'Open',
            },
            {
              fieldId: StandardIds.OpportunitySourceName,
              value: 'CRM UI',
            },
          ]),
        ),
        selectAllTableActions$: accountGroupService.currentAccountGroupId$.pipe(
          map((accountGroupId) => [
            ExportSelectAllRowAction(accountGroupId, exportActionsService, 'Opportunity', 'ACTIONS.CRM.EXPORT.LABEL'),
          ]),
        ),
      },
      lists: {
        canStartManualAutomation$: accessService.canRunManualAutomations$,
        singleRowTableActions$: combineLatest([
          namespace$,
          currentUserId$,
          authService.isSuperAdmin$,
          featureFlagService.checkFeatureFlag(partnerId, '', EphemeralAutomationsFeature),
          accessService.canRunManualAutomations$,
        ]).pipe(
          map(([namespace, currentUserId, isUserSuperAdmin, canRunEphemeralAutomations, canStartManualAutomation]) => {
            const actions: SingleRowAction[] = [];
            if (canStartManualAutomation && canRunEphemeralAutomations) {
              actions.push({
                label: 'COMPANY.LIST_OBJECTS_TABLE.ACTIONS.RUN_BULK_ACTION',
                callback: async (row: Row) => {
                  const listId = row.id;
                  const resourceType = (row.data as any)?.resourceType || 'Contact';

                  let entityType: EntityType = EntityType.ENTITY_TYPE_COMPANY;
                  if (resourceType === 'Contact') {
                    entityType = EntityType.ENTITY_TYPE_CONTACT;
                  } else if (resourceType.startsWith('CustomObject::')) {
                    entityType = EntityType.ENTITY_TYPE_CUSTOM_OBJECT;
                  }

                  const partnerId = await firstValueFrom(parentNamespace$);
                  let userId = currentUserId;
                  if (isUserSuperAdmin) {
                    userId = await automationActionsService.openPartnerServiceAccountSelectorDialog(partnerId);
                    if (!userId) {
                      return;
                    }
                  }

                  // Create ephemeral automation (open sidebar for the user to select an action and return the automation ID)
                  const automationId = await automationActionsService.buildEphemeralAutomationToRun(
                    namespace,
                    userId,
                    entityType,
                  );
                  if (!automationId) {
                    return;
                  }

                  // Start automation on the list
                  const listRequest = {
                    namespace: namespace,
                    listId: listId,
                    automationId: automationId,
                  };

                  await firstValueFrom(listItemsApiService.startManualAutomations(listRequest));
                },
              });
            }
            return actions;
          }),
        ),
      },
      customobject: {
        showRightHandProfilePanel$: customObjectProfileCards$.pipe(map((cards) => cards.length > 0)),
        showOpportunityAssociation$: of(true),
        profileCards$: customObjectProfileCards$,
        baseColumnIds: [StandardIds.CustomObjectName],
        getDisplayName: (crmObject: CrmObjectInterface) => {
          return crmObjectDisplayService.customObjectDisplayName(crmObject);
        },
        createAssociationFields: {
          objectTypes: ['Contact', 'Company', 'Opportunity', 'CustomObject'],
          required: false,
        },
        formCustomInputs: [
          {
            fieldId: SystemFieldIds.CustomObjectOwnerID,
            component: TeamMemberFormInputComponent,
          },
        ],
        filterInputOverrides$: of([
          {
            title: translationService.instant('CRM.CUSTOM_FILTERS.OWNER'),
            fieldId: SystemFieldIds.CustomObjectOwnerID,
            filterInput: CrmUserFilterInputComponent,
            filterChip: CrmUserFilterChipComponent,
          },
        ]),
        tableCustomCells: [
          {
            fieldIds: [SystemFieldIds.CustomObjectOwnerID],
            columnDefinition: {
              id: SystemFieldIds.CustomObjectOwnerID,
            },
            customCellComponent: CrmUserColumnCellComponent,
          },
        ],
        formDefaultOnCreateInputValues$: currentUserId$.pipe(
          map((userId) => [
            {
              fieldId: SystemFieldIds.CustomObjectOwnerID,
              value: userId,
            },
          ]),
        ),
        selectAllTableActions$: bulkCustomObjectSelectAllTableActions(accountGroupService, bulkDeleteService),
        showHistoryButton$: authService.isSuperAdmin$.pipe(shareReplay({ refCount: true, bufferSize: 1 })),
      },
      services: {
        userService: userService,
        accessService: accessService,
        pipelineService: pipelineService,
        aiAssistantService: aiAssistantService,
        voiceApiService: voiceApiService,
        defaultAssistantAvatarIcon: DEFAULT_AVATAR_SVG_ICON,
      },

      meetingAction: (contactId, currentUserId, namespace, parentNamespace) => {
        meetingEventService.openMeetingEventListDialog(contactId, currentUserId, namespace, parentNamespace);
      },

      isMeetingIconNeeded$: isMeetingSchedulerConfigured$,

      hasAccessToFeature$: (feature) => hasAccessToFeature$(feature, partnerMarketConfigService, activeAppService),
      hasInboxAccess$: of(true),
      canShowInboxForContact$: partnerMarketConfigService.businessAppConfig$.pipe(
        map((config) => config.showInboxMessage),
        catchError(() => of(false)),
      ),
      openContactConversation: async (
        _namespace: string,
        parentNamespace: string,
        contactId: string,
        channel: ConversationChannel,
      ) => {
        const accountGroupNamespace = await firstValueFrom(namespaceResolutionService.originalAccountGroupId$);
        const partnerNamespace = parentNamespace || partnerId || '';
        await crmInboxService.goToContactConversation(accountGroupNamespace, partnerNamespace, contactId, channel);
      },
      showLeadProspector$: of(false),
      customObjectTypes$: getCustomObjectTypes(namespace$, customObjectTypeService),
      automationsSideMenuComponent: AutomationsSideMenuDrawerComponent,
      connectedEmailProvider$: combineLatest([gmailConnected$, outlookConnected$]).pipe(
        map(([gmail, outlook]): 'gmail' | 'outlook' | null => {
          if (gmail) return 'gmail';
          if (outlook) return 'outlook';
          return null;
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      ),
      openEmailReply: async (
        contactIds: string[],
        emails: string[],
        subject: string,
        body?: string,
        visibility?: string,
        threadId?: string,
        sourceProvider?: string,
        associations?: ActivityAssociationInterface[],
      ) => {
        const [ns, uid, gmailConns, outlookConns] = await firstValueFrom(
          combineLatest([namespace$, currentUserId$, gmailConnectionOptions$, outlookConnectionOptions$]),
        );
        if (gmailConns.length === 0 && outlookConns.length === 0) return;
        const src = sourceProvider?.toLowerCase() || '';
        let provider: 'gmail' | 'outlook';
        if (src.includes('outlook') && outlookConns.length > 0) {
          provider = 'outlook';
        } else if (src.includes('gmail') && gmailConns.length > 0) {
          provider = 'gmail';
        } else {
          provider = gmailConns.length > 0 ? 'gmail' : 'outlook';
        }
        const connections = provider === 'gmail' ? gmailConns : outlookConns;
        emailWorkflowService.openEmailReply(
          ns,
          uid,
          contactIds,
          emails,
          provider,
          subject,
          body,
          visibility,
          threadId,
          associations,
          connections,
        );
      },
    } as CrmDependencies;
  },
});

export const CrmMultilocationInjectionToken = new InjectionToken<CrmMultiLocationDependencies>(
  'dependencies for multilocation crm',
  {
    factory: () => {
      const locationsService = inject(LocationsService);
      const accountGroupService = inject(AccountGroupService);
      const router = inject(Router);

      const currentLocation$ = locationsService.currentLocation$.pipe(
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const groupId$ = currentLocation$.pipe(
        map((location) => {
          return isBrand(location) ? (location as Brand).groupNodes[0] : '';
        }),
      );

      const multiLocationContext$ = combineLatest([groupId$, locationsService.currentAccountGroups$]).pipe(
        map(([groupId, accountGroups]) => {
          if (!groupId) {
            return {} as MultiLocationContext;
          }

          return {
            groupId: groupId,
            accountGroupsIds: Object.keys(accountGroups).map((accountGroupId) => accountGroupId),
          } as MultiLocationContext;
        }),
      );

      const isMultiLocation$ = multiLocationContext$.pipe(map((context) => !!context.groupId));

      const routePrefix$ = groupId$.pipe(
        map((groupID) => {
          return `/account/brands/${groupID}`;
        }),
      );

      const accountGroupIdFromRoute$: Observable<string> = router.events.pipe(
        filter((event) => event instanceof GuardsCheckStart),
        map((event: GuardsCheckStart) => extractAccountGroupIdFromRoute(event.url)),
      );

      const namespace$: Observable<string> = locationsService.isCurrentLocationABrand$.pipe(
        switchMap((isBrand) => {
          if (isBrand) {
            return accountGroupIdFromRoute$;
          } else {
            return of('');
          }
        }),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const currentLocationName$ = namespace$.pipe(
        switchMap((namespace) => accountGroupService.getAccountGroup(namespace)),
        map((accountGroup) => accountGroup?.companyName || ''),
      );

      return {
        multiLocationContext$,
        routePrefix$,
        currentLocationName$,
        isMultiLocation$,
      };
    },
  },
);

function objectViewPresets$(
  translationService: TranslateService,
  translateByObjectService: TranslateForCrmObjectService,
  ownerId$: Observable<string>,
  objectType: ObjectType,
  lastEngagedFieldId: string,
  ownerFieldId: string,
): Observable<PresetFilter[]> {
  const myFilterName$ = translateByObjectService.getTranslationForCrmObject(objectType, 'MY_OBJECTS');
  const myFilter$ = combineLatest([ownerId$, myFilterName$]).pipe(
    map(([userId, filterName]) => {
      if (userId) {
        const filter: GalaxyFilterInterface = {
          fieldId: ownerFieldId,
          operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
          values: [{ string: userId }],
        };
        return {
          name: filterName,
          filters: [filter],
          id: 'owned-objects',
        } as PresetFilter;
      }
      return undefined;
    }),
  );

  const allFilter$ = translateByObjectService.getTranslationForCrmObject(objectType, 'ALL_OBJECTS').pipe(
    map((filterName) => {
      return {
        name: filterName,
        filters: [],
        id: 'all-objects',
      } as PresetFilter;
    }),
  );

  return combineLatest([myFilter$, allFilter$]).pipe(
    map(([myFilter, allFilter]) => {
      const filters: PresetFilter[] = [];
      if (myFilter) {
        filters.push(myFilter);
      }
      if (allFilter) {
        filters.push(allFilter);
      }

      // recently engaged
      const lastEngagedFilter = {
        name: translationService.instant('CRM.PRESET_FILTERS.RECENTLY_ENGAGED'),
        filters: [
          {
            fieldId: lastEngagedFieldId,
            operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_AFTER,
            values: [{ dateDefault: GalaxyDateDefault.DATE_DEFAULT_LAST_WEEK }],
          },
        ],
        id: 'recently-engaged',
      } as PresetFilter;
      filters.push(lastEngagedFilter);

      return filters;
    }),
  );
}

function filterActivityInputOverrides$(translationService: TranslateService): Observable<CRMFilterOverride[]> {
  return of([
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.OWNER'),
      fieldId: SystemFieldIds.ActivityOwnerID,
      filterInput: TeamMemberFilterInputComponent,
      filterChip: TeamMemberFilterChipComponent,
    },
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.CALL_DURATION'),
      fieldId: StandardIds.ActivityCallDuration,
      filterInput: DurationCustomFilterInputComponent,
      filterChip: DurationCustomFilterChipComponent,
    },
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.MEETING_DURATION'),
      fieldId: StandardIds.ActivityMeetingDuration,
      filterInput: DurationCustomFilterInputComponent,
      filterChip: DurationCustomFilterChipComponent,
    },
  ] as CRMFilterOverride[]);
}

function filterContactInputOverrides$(translationService: TranslateService): Observable<CRMFilterOverride[]> {
  return of([
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.PRIMARY_COMPANY'),
      fieldId: StandardIds.ContactPrimaryCompanyID,
      filterInput: CompanyCustomFilterInputComponent,
      filterChip: CompanyCustomFilterChipComponent,
    },
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.OWNER'),
      fieldId: SystemFieldIds.ContactOwnerID,
      filterInput: TeamMemberFilterInputComponent,
      filterChip: TeamMemberFilterChipComponent,
    },
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.TEAM_MEMBERS'),
      fieldId: StandardIds.ContactAdditionalTeamMemberIDs,
      filterInput: AdditionalTeamMemberFilterInputComponent,
      filterChip: TeamMemberFilterChipComponent,
    },
  ] as CRMFilterOverride[]);
}

function filterCompanyInputOverride$(translationService: TranslateService): Observable<CRMFilterOverride[]> {
  return of([
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.PARENT_COMPANY'),
      fieldId: StandardIds.CompanyParentCompanyID,
      filterInput: CompanyCustomFilterInputComponent,
      filterChip: CompanyCustomFilterChipComponent,
    },
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.OWNER'),
      fieldId: SystemFieldIds.CompanyOwnerID,
      filterInput: TeamMemberFilterInputComponent,
      filterChip: TeamMemberFilterChipComponent,
    },
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.TEAM_MEMBERS'),
      fieldId: StandardIds.CompanyAdditionalTeamMemberIDs,
      filterInput: AdditionalTeamMemberFilterInputComponent,
      filterChip: TeamMemberFilterChipComponent,
    },
    {
      title: translationService.instant('CRM.CUSTOM_FILTERS.CATEGORIES'),
      fieldId: StandardIds.CompanyCategoryIDs,
      filterInput: CategoriesFilterInputComponent,
      filterChip: CategoriesFilterChipComponent,
    },
  ] as CRMFilterOverride[]);
}

function contactTableCustomCells(translationService: TranslateService): TableCustomCell[] {
  return [
    {
      fieldIds: [SystemFieldIds.ContactOwnerID],
      columnDefinition: {
        id: SystemFieldIds.ContactOwnerID,
        title: translationService.instant('CRM.CUSTOM_FILTERS.OWNER'),
      },
      customCellComponent: OwnerCellComponent,
    },
    {
      fieldIds: [StandardIds.ContactAdditionalTeamMemberIDs],
      columnDefinition: {
        id: StandardIds.ContactAdditionalTeamMemberIDs,
        title: translationService.instant('CRM.CUSTOM_FILTERS.TEAM_MEMBERS'),
      },
      customCellComponent: TeamMemberCellComponent,
    },
  ] as TableCustomCell[];
}

function companyTableCustomCells(translationService: TranslateService): TableCustomCell[] {
  return [
    {
      fieldIds: [SystemFieldIds.CompanyOwnerID],
      columnDefinition: {
        id: SystemFieldIds.CompanyOwnerID,
        title: translationService.instant('CRM.CUSTOM_FILTERS.OWNER'),
      },
      customCellComponent: OwnerCellComponent,
    },
    {
      fieldIds: [StandardIds.CompanyAdditionalTeamMemberIDs],
      columnDefinition: {
        id: StandardIds.CompanyAdditionalTeamMemberIDs,
        title: translationService.instant('CRM.CUSTOM_FILTERS.TEAM_MEMBERS'),
      },
      customCellComponent: TeamMemberCellComponent,
    },
    {
      fieldIds: [StandardIds.CompanyCategoryIDs],
      columnDefinition: {
        id: 'customCategoriesColumn',
        title: translationService.instant('CRM.CUSTOM_FILTERS.CATEGORIES'),
      },
      customCellComponent: CategoriesCellComponent,
    },
  ] as TableCustomCell[];
}

function hasAccessToFeature$(
  feature: string,
  partnerMarketConfigService: PartnerMarketConfigService,
  productService: ProductService,
): Observable<boolean> {
  if (feature === 'crm-ai-summary') {
    return productService.activeProducts$.pipe(
      map((products) => products.some((product) => product.productId === productIds.crmAi)),
    );
  }

  return feature == 'task-queue'
    ? of(true)
    : partnerMarketConfigService.hasAccessToFeature(feature as Feature).pipe(startWith(false));
}

function buildContactHeaders$(
  partnerMarketConfigService: PartnerMarketConfigService,
  productService: ProductService,
): Observable<ProfileHeader[]> {
  const leadScoringFeature$ = hasAccessToFeature$('crm-lead-scoring', partnerMarketConfigService, productService);
  const leadScoringConfig$ = partnerMarketConfigService.businessAppConfig$.pipe(
    map((config) => config.showLeadScoring),
  );
  return combineLatest([leadScoringFeature$, leadScoringConfig$]).pipe(
    map(([leadScoringFeature, leadScoringConfig]) => {
      const headers: ProfileHeader[] = [];
      headers.push({ component: NpsBadgeComponent });
      if (leadScoringFeature && leadScoringConfig) {
        headers.push({ component: LeadScoreBadgeComponent });
      }
      return headers;
    }),
  );
}

function getCustomObjectTypes(
  namespace: Observable<string>,
  crmCustomObjectTypeApiService: CRMCustomObjectTypeApiService,
): Observable<CustomObjectType[]> {
  return namespace.pipe(
    switchMap((ns) => {
      return crmCustomObjectTypeApiService.listCustomObjectTypes({
        namespace: ns,
      });
    }),
    map((customObjectTypes) => {
      return customObjectTypes?.customObjectTypes || [];
    }),
  );
}

function bulkTaskSelectAllTableActions(
  accountGroupService: AccountGroupService,
  bulkDeleteService: BulkDeleteService,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroupId$.pipe(
    map((accountGroupId) => {
      const actions: MultiRowAction[] = [];
      actions.push(
        BulkDeleteAll(accountGroupId, bulkDeleteService, 'Activity', 'LIST_OBJECTS_TABLE.BULK_DELETE.LABEL'),
      );
      return actions;
    }),
  );
}
function bulkCustomObjectSelectAllTableActions(
  accountGroupService: AccountGroupService,
  bulkDeleteService: BulkDeleteService,
): Observable<MultiRowAction[]> {
  return accountGroupService.currentAccountGroupId$.pipe(
    map((accountGroupId) => {
      const actions: MultiRowAction[] = [];
      actions.push(
        BulkDeleteAll(accountGroupId, bulkDeleteService, 'CustomObject', 'LIST_OBJECTS_TABLE.BULK_DELETE.LABEL'),
      );
      return actions;
    }),
  );
}
